import { useState } from 'react'
import { Plus, Edit2, Trash2, Eye, EyeOff, Play, Mic2, ExternalLink } from 'lucide-react'
import { useCollection, useFileUpload } from '../../hooks/useFirestore'
import { sermons as defaultSermons } from '../../data/seedData'
import { formatDate, getYouTubeThumbnail } from '../../utils/helpers'
import { Button, Modal, Input, Textarea, Card, Badge, Checkbox } from '../../components/common'

const SermonForm = ({ sermon, onSave, onClose }) => {
  const [formData, setFormData] = useState(sermon || {
    title: '',
    speaker: '',
    series: '',
    date: '',
    youtubeUrl: '',
    description: '',
    scriptures: '',
    notesUrl: '',
    thumbnailUrl: '',
    isPublished: true,
  })
  const [saving, setSaving] = useState(false)
  const { upload, uploading } = useFileUpload()

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    const result = await upload(file, `sermons/${Date.now()}_${file.name}`)
    if (result.success) {
      setFormData({ ...formData, thumbnailUrl: result.url })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    await onSave(formData)
    setSaving(false)
  }

  // Auto-fetch YouTube thumbnail
  const fetchYouTubeThumbnail = () => {
    const thumbnail = getYouTubeThumbnail(formData.youtubeUrl)
    if (thumbnail) {
      setFormData({ ...formData, thumbnailUrl: thumbnail })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Sermon Title"
        required
        value={formData.title}
        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        placeholder="The Power of Faith"
      />

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Speaker"
          required
          value={formData.speaker}
          onChange={(e) => setFormData({ ...formData, speaker: e.target.value })}
          placeholder="Pastor John Adebayo"
        />
        <Input
          label="Series (optional)"
          value={formData.series}
          onChange={(e) => setFormData({ ...formData, series: e.target.value })}
          placeholder="Faith Foundations"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Date"
          type="date"
          required
          value={formData.date}
          onChange={(e) => setFormData({ ...formData, date: e.target.value })}
        />
        <div>
          <Input
            label="YouTube URL"
            required
            value={formData.youtubeUrl}
            onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
            placeholder="https://youtube.com/watch?v=..."
          />
          {formData.youtubeUrl && (
            <button
              type="button"
              onClick={fetchYouTubeThumbnail}
              className="text-sm text-primary-600 hover:text-primary-700 mt-1"
            >
              Fetch YouTube Thumbnail
            </button>
          )}
        </div>
      </div>

      <Textarea
        label="Description"
        rows={3}
        value={formData.description}
        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
        placeholder="Brief description of the sermon..."
      />

      <Input
        label="Scripture References"
        value={formData.scriptures}
        onChange={(e) => setFormData({ ...formData, scriptures: e.target.value })}
        placeholder="Hebrews 11:1-6, Mark 11:22-24"
      />

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Thumbnail Image
          </label>
          <div className="flex items-center gap-4">
            {formData.thumbnailUrl && (
              <img
                src={formData.thumbnailUrl}
                alt="Preview"
                className="w-24 h-16 object-cover rounded-lg"
              />
            )}
            <label className="px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
              <span className="text-sm">{uploading ? 'Uploading...' : 'Upload'}</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleThumbnailUpload}
                className="hidden"
              />
            </label>
          </div>
        </div>
        <Input
          label="Sermon Notes URL (optional)"
          value={formData.notesUrl}
          onChange={(e) => setFormData({ ...formData, notesUrl: e.target.value })}
          placeholder="https://drive.google.com/..."
        />
      </div>

      <Checkbox
        label="Published"
        checked={formData.isPublished}
        onChange={(e) => setFormData({ ...formData, isPublished: e.target.checked })}
      />

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={saving}>
          {sermon ? 'Update Sermon' : 'Add Sermon'}
        </Button>
      </div>
    </form>
  )
}

const SermonsManager = () => {
  const [showModal, setShowModal] = useState(false)
  const [editingSermon, setEditingSermon] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const { data: sermons, add, update, remove } = useCollection('sermons', {
    orderByField: 'date',
    orderDirection: 'desc',
  })

  const displaySermons = sermons?.length > 0 ? sermons : defaultSermons

  const handleSave = async (data) => {
    if (editingSermon?.id) {
      await update(editingSermon.id, data)
    } else {
      await add(data)
    }
    setShowModal(false)
    setEditingSermon(null)
  }

  const handleDelete = async () => {
    if (deleteConfirm) {
      await remove(deleteConfirm)
      setDeleteConfirm(null)
    }
  }

  const togglePublished = async (sermon) => {
    await update(sermon.id, { isPublished: !sermon.isPublished })
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Sermons</h1>
          <p className="text-gray-500">Manage sermon archive and uploads</p>
        </div>
        <Button
          variant="primary"
          icon={Plus}
          onClick={() => {
            setEditingSermon(null)
            setShowModal(true)
          }}
        >
          Add Sermon
        </Button>
      </div>

      {/* Sermons Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displaySermons.map((sermon) => (
          <Card key={sermon.id} animate={false} padding={false} className="group">
            <div className="relative aspect-video">
              <img
                src={sermon.thumbnailUrl || getYouTubeThumbnail(sermon.youtubeUrl) || ''}
                alt={sermon.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <a
                  href={sermon.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-white rounded-full flex items-center justify-center"
                >
                  <Play className="w-5 h-5 text-primary-900 ml-0.5" fill="currentColor" />
                </a>
              </div>
              {!sermon.isPublished && (
                <div className="absolute top-2 left-2">
                  <Badge variant="gray">Draft</Badge>
                </div>
              )}
            </div>

            <div className="p-4">
              <h3 className="font-semibold text-dark-100 mb-1 line-clamp-1">{sermon.title}</h3>
              <p className="text-sm text-gray-500 mb-2">{sermon.speaker}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-400">{formatDate(sermon.date, 'MMM d, yyyy')}</span>
                  {sermon.series && (
                    <Badge variant="secondary" size="sm">{sermon.series}</Badge>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => togglePublished(sermon)}
                    className={`p-1.5 rounded-lg transition-colors ${
                      sermon.isPublished
                        ? 'text-emerald-600 hover:bg-emerald-50'
                        : 'text-gray-400 hover:bg-gray-100'
                    }`}
                  >
                    {sermon.isPublished ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => {
                      setEditingSermon(sermon)
                      setShowModal(true)
                    }}
                    className="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(sermon.id)}
                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {displaySermons.length === 0 && (
        <div className="text-center py-12 text-gray-500 bg-white rounded-xl">
          <Mic2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No sermons yet</p>
          <Button
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() => setShowModal(true)}
          >
            Add Your First Sermon
          </Button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingSermon(null)
        }}
        title={editingSermon ? 'Edit Sermon' : 'Add Sermon'}
        size="lg"
      >
        <SermonForm
          sermon={editingSermon}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false)
            setEditingSermon(null)
          }}
        />
      </Modal>

      {/* Delete Confirmation */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Delete Sermon"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Are you sure you want to delete this sermon? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default SermonsManager
